import {useEffect, useMemo, useRef} from "react";

// useParsedSvgRoot parses an SVG document from the given string and returns the root <svg> element, or null
export function useParsedSvgRootDOMElement(svg) {
  return useMemo(() => {
    if (!svg) {
      return null;
    }

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(svg, "image/svg+xml");

      // doc is a Document object (specifically SVGDocument), so grab the root <svg> Element (specifically SVGSVGElement)
      // https://developer.mozilla.org/en-US/docs/Web/API/SVGSVGElement
      return doc.documentElement;
    } catch (error) {
      console.error("Parsing SVG failed:", error);
      return null;
    }
  }, [svg]);
}

// useDOMElementContainerReference returns a reference that can be assigned to some React element `ref` attribute, 
// that will be used as a container for `domElement`, a raw DOM element.
// Note that this code assumes the element doesn't have any other children: it will remove/replace an existing child
export function useDOMElementContainerReference(domElement) {
  const containerRef = useRef();

  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      console.warn("useDOMElementContainerReference: No containerRef yet", {domElement});
      return;
    }

    const existingChild = container.firstChild;

    if (!domElement) {
      if (existingChild) {
        container.removeChild(existingChild);
      }
      return;
    }

    try {
      if (existingChild) {
        existingChild.replaceWith(domElement);
      } else {
        container.appendChild(domElement);
      }
    } catch (error) {
      console.error("Error updating DOM element container:", error);
    }
  }, [containerRef, domElement]);

  return containerRef;
}